import {
  getDashboardViewId,
  getLandingViewId,
  resolveBusinessIdentityId,
  resolveIdentityRoute,
  resolveIdentityView,
} from './identityRegistry';

export interface IdentityTarget {
  appView: string;
  path: string;
}

export interface AuthSuccessTargetInput {
  signupSector?: string | null;
  demoSector?: string | null;
  userSector?: string | null;
  hasCompletedOnboarding: boolean;
}

function buildBusinessDashboardTarget(identityId: string): IdentityTarget {
  const canonicalId = resolveBusinessIdentityId(identityId) ?? identityId;
  return {
    appView: getDashboardViewId(canonicalId),
    path: `/ecosystem/sector/${canonicalId}`,
  };
}

function buildBusinessLandingTarget(identityId: string): IdentityTarget {
  const canonicalId = resolveBusinessIdentityId(identityId) ?? identityId;
  return {
    appView: getLandingViewId(canonicalId),
    path: `/anchor-${canonicalId}`,
  };
}

export function resolveIdentityPathTarget(pathname: string): IdentityTarget | null {
  const route = resolveIdentityRoute(pathname);
  return route ? { appView: route.appView, path: route.path } : null;
}

export function resolveEcoNavIdentityRoute(view: string): IdentityTarget | null {
  const route = resolveIdentityView(view);
  return route ? { appView: route.appView, path: route.path } : null;
}

export function getAuthSuccessTarget({
  signupSector,
  demoSector,
  userSector,
  hasCompletedOnboarding,
}: AuthSuccessTargetInput): IdentityTarget {
  const signupIdentity = resolveBusinessIdentityId(signupSector);
  if (signupIdentity) return buildBusinessDashboardTarget(signupIdentity);

  const demoIdentity = resolveBusinessIdentityId(demoSector);
  if (demoIdentity) return buildBusinessDashboardTarget(demoIdentity);

  const userIdentity = resolveBusinessIdentityId(userSector);
  if (userIdentity) return buildBusinessDashboardTarget(userIdentity);

  return hasCompletedOnboarding
    ? { appView: 'dashboard', path: '/dashboard' }
    : { appView: 'onboarding', path: '/onboarding' };
}

export interface LogoutTargetInput {
  currentPath: string;
  demoSector?: string | null;
  userSector?: string | null;
  organizationSector?: string | null;
}

export function getLogoutTarget({
  currentPath,
  demoSector,
  userSector,
  organizationSector,
}: LogoutTargetInput): IdentityTarget {
  const routeIdentity = resolveIdentityRoute(currentPath)?.identity;
  const businessIdentity =
    resolveBusinessIdentityId(demoSector) ||
    resolveBusinessIdentityId(userSector) ||
    resolveBusinessIdentityId(organizationSector) ||
    (routeIdentity?.kind === 'business' || routeIdentity?.kind === 'internal'
      ? routeIdentity.id
      : null);

  if (businessIdentity) {
    return buildBusinessLandingTarget(businessIdentity);
  }

  return { appView: 'anchor-ai-guard', path: '/anchor-ai-guard' };
}
